import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { FileCategory } from '../../shared/models';
import { AuthService } from './auth.service';
import { EditorMediaProbeService } from '../../features/editor/services/editor-media-probe.service';
import { validateFileForCategory } from '../../shared/utils/file-category';
import {
	DuplicateResolutionEntry,
	MediaMetadata,
	UploadItem,
	UploadResponse,
	UploadStats,
	UploadStatus,
} from './upload.types';

/** Human-readable byte size — `1.2 MB`, `650 KB`, `2.1 GB`. One decimal
 * once we're past KB so the displayed number always has useful precision. */
function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${Math.round(kb)} KB`;
	const mb = kb / 1024;
	if (mb < 1024) return `${mb.toFixed(1)} MB`;
	const gb = mb / 1024;
	return `${gb.toFixed(1)} GB`;
}

const CONCURRENCY = 3;
const PROBE_CONCURRENCY = 3;
const MAX_AUTO_RETRIES = 3;
const RETRY_DELAYS_MS = [2000, 4000, 8000];
const RETRY_COUNTDOWN_TICK_MS = 250;
const CANCEL_ALL_CONFIRM_THRESHOLD = 10;
const BATCH_WARNING_THRESHOLD = 200;
const PROBE_CATEGORIES: FileCategory[] = ['video', 'mask', 'audios'];
const ACTIVE_STATUSES: UploadStatus[] = ['queued', 'probing', 'uploading'];
const TERMINAL_STATUSES: UploadStatus[] = ['done', 'error', 'cancelled'];

/** Backend response shape for POST /variants/upload (single file per request). */
interface VariantUploadResponse {
	added: number;
	errors: string[];
	media?: unknown;
}

/**
 * Root-scoped upload queue with bounded concurrency, per-file progress, retry,
 * and cancellation. Phase 1: core service + file-browser delegation only. No UI
 * panel (Phase 2), no optimistic placeholders (Phase 3), no client-side
 * validation or tab-close guard (Phase 4).
 */
@Injectable({ providedIn: 'root' })
export class UploadService {
	private readonly auth = inject(AuthService);
	private readonly probe = inject(EditorMediaProbeService);
	private readonly messages = inject(MessageService);

	readonly items = signal<UploadItem[]>([]);

	/**
	 * Bumped whenever something happens that should force the upload panel to
	 * un-collapse (currently: a Replace upload failed). The panel runs an
	 * effect on this signal and clears its `collapsed` flag when it changes.
	 *
	 * Replace items deliberately don't get a placeholder in the file-browser
	 * grid (the existing row already represents them), so without this the
	 * only failure feedback is the global toast counter and a row in the
	 * collapsed-by-default panel — easy to miss.
	 */
	readonly panelExpandRequest = signal(0);

	/** Semaphore limiting concurrent mediabunny probes (D10 — cap at 3 to avoid
	 * CPU/memory spikes on 20-variant groups). Tracks active probe count and a
	 * resolver queue; each `processItem` awaits `acquireProbeSlot()` before
	 * calling `probe.probeFile()` and releases in the finally block. */
	private activeProbes = 0;
	private readonly probeWaiters: Array<() => void> = [];

	constructor() {
		// Tab-close guard: arm a native beforeunload listener while any upload is
		// actually in flight (queued/probing/uploading). Terminal items don't keep
		// the listener armed. Browsers ignore custom messages on this event since
		// ~2017, so we just signal intent via preventDefault + returnValue.
		effect(() => {
			if (this.isActive()) {
				window.addEventListener('beforeunload', this.beforeUnloadHandler);
			} else {
				window.removeEventListener('beforeunload', this.beforeUnloadHandler);
			}
		});
	}

	readonly isActive = computed(() =>
		this.items().some(i => ACTIVE_STATUSES.includes(i.status())),
	);

	readonly stats = computed<UploadStats>(() => {
		const all = this.items();
		const total = all.length;
		const done = all.filter(i => i.status() === 'done').length;
		const failed = all.filter(i => i.status() === 'error').length;
		const active = all.filter(i => i.status() === 'uploading').length;
		const queued = all.filter(i => i.status() === 'queued').length;
		const progressOverall = total === 0
			? 0
			: Math.round(
				all.reduce((a, i) => a + (i.status() === 'done' ? 100 : i.progress()), 0) / total,
			);
		return { total, done, failed, active, queued, progressOverall };
	});

	/**
	 * Enqueue one or more files for upload.
	 *
	 * - `existingFilenames` (legacy): snapshot of names already present in the
	 *   target project+category. Matching filenames flag `duplicateWarning`
	 *   only — the upload proceeds as a fresh document. Kept for callers that
	 *   want the passive icon without the prompt-and-replace dialog.
	 * - `duplicateResolution`: per-filename decision from the upload-duplicate
	 *   dialog. `action: 'replace'` carries the existing media id and routes
	 *   the request through the backend's replace-in-place path; `action: 'new'`
	 *   matches current append-as-fresh behaviour.
	 *
	 * Client-side validation (type + size) runs synchronously. Rejected files
	 * are pushed with terminal `error` status and `nonRetriable = true` so the
	 * UI hides retry; they never enter the concurrency queue.
	 */
	enqueue(
		files: FileList | File[],
		projectSlug: string,
		category: FileCategory,
		options?: {
			existingFilenames?: Set<string>;
			duplicateResolution?: Map<string, DuplicateResolutionEntry>;
		},
	): void {
		const list = Array.from(files);
		if (!list.length) return;

		if (list.length > BATCH_WARNING_THRESHOLD) {
			this.messages.add({
				severity: 'warn',
				summary: 'Large batch',
				detail: `${list.length} files — this may take a while.`,
				life: 6000,
			});
		}

		const existingFilenames = options?.existingFilenames;
		const resolution = options?.duplicateResolution;

		const maxBytes = environment.maxUploadSizeMb * 1024 * 1024;
		const newItems = list.map(file => {
			const typeError = validateFileForCategory(file, category);
			const sizeError = !typeError && file.size > maxBytes
				? `File too large: ${formatBytes(file.size)} (max ${environment.maxUploadSizeMb} MB)`
				: null;
			const validationError = typeError ?? sizeError;
			if (validationError) {
				return this.createRejectedItem(file, projectSlug, category, validationError);
			}
			const decision = resolution?.get(file.name);
			const replaceMediaId = decision?.action === 'replace' ? decision.replaceMediaId : undefined;
			// A confirmed Replace clears the passive duplicate-warning icon —
			// the user already opted in, no warning needed on the placeholder.
			const duplicate = decision
				? false
				: existingFilenames?.has(file.name) ?? false;
			return this.createItem(file, projectSlug, category, duplicate, replaceMediaId);
		});

		this.items.update(items => [...items, ...newItems]);
		queueMicrotask(() => this.processQueue());
	}

	/**
	 * Enqueue files as variants of an existing group. Each file becomes its own
	 * UploadItem tagged with the group so the upload panel can render them as a
	 * collapsed group row (D1) and the backend receives one single-file request
	 * per variant (D8). Duplicate detection is currently bypassed for variants —
	 * the backend rejects duplicate variant names with an error per file.
	 */
	enqueueVariantGroup(
		groupId: string,
		groupName: string,
		groupCategory: FileCategory,
		files: FileList | File[],
		projectSlug: string,
	): void {
		const list = Array.from(files);
		if (!list.length) return;

		const maxBytes = environment.maxUploadSizeMb * 1024 * 1024;
		const newItems = list.map(file => {
			const typeError = validateFileForCategory(file, groupCategory);
			const sizeError = !typeError && file.size > maxBytes
				? `File too large: ${formatBytes(file.size)} (max ${environment.maxUploadSizeMb} MB)`
				: null;
			const validationError = typeError ?? sizeError;
			if (validationError) {
				const rejected = this.createRejectedItem(file, projectSlug, groupCategory, validationError);
				rejected.groupId = groupId;
				rejected.groupName = groupName;
				rejected.groupCategory = groupCategory;
				return rejected;
			}
			const item = this.createItem(file, projectSlug, groupCategory, false);
			item.groupId = groupId;
			item.groupName = groupName;
			item.groupCategory = groupCategory;
			return item;
		});

		this.items.update(items => [...items, ...newItems]);
		queueMicrotask(() => this.processQueue());
	}

	/** Cancel every pending (queued/probing/uploading) child of a variant group.
	 * Done / errored / cancelled children are left alone (D11). */
	cancelGroup(groupId: string): void {
		for (const item of this.items()) {
			if (item.groupId !== groupId) continue;
			if (ACTIVE_STATUSES.includes(item.status())) this.cancel(item.id);
		}
	}

	/** Retry every retriable failed child of a variant group (D11). Validation
	 * failures (`nonRetriable`) stay terminal. */
	retryGroup(groupId: string): void {
		for (const item of this.items()) {
			if (item.groupId !== groupId) continue;
			if (item.status() === 'error' && !item.nonRetriable) this.retry(item.id);
		}
	}

	cancel(itemId: string): void {
		const item = this.items().find(i => i.id === itemId);
		if (!item) return;
		// Once an item is terminal (done / error / cancelled), the only way to
		// remove it is to dismiss — cancel is a no-op. This keeps the grid ✕
		// on a validation-error placeholder behaving sensibly without a second
		// handler: the template routes it through `dismissItem()` instead.
		if (TERMINAL_STATUSES.includes(item.status())) return;
		this.clearRetry(item);
		if (item.xhr) {
			item.xhr.abort();
			item.xhr = null;
		}
		if (item.previewUrl) {
			URL.revokeObjectURL(item.previewUrl);
			item.previewUrl = null;
		}
		item.status.set('cancelled');
		queueMicrotask(() => this.processQueue());
	}

	/**
	 * Cancel every non-terminal upload. The caller is expected to have asked for
	 * user confirmation when the active count exceeds CANCEL_ALL_CONFIRM_THRESHOLD
	 * — pass `confirmed: true` in that case. Direct calls above the threshold
	 * without confirmation are refused with a warning (confirm is a UI concern;
	 * the service just enforces the contract).
	 */
	cancelAll(confirmed = false): void {
		const activeCount = this.items().filter(i => ACTIVE_STATUSES.includes(i.status())).length;
		if (activeCount > CANCEL_ALL_CONFIRM_THRESHOLD && !confirmed) {
			 
			console.warn(
				`UploadService.cancelAll(): refused — ${activeCount} active uploads exceed the ` +
				`${CANCEL_ALL_CONFIRM_THRESHOLD}-item confirmation threshold. ` +
				`Pass cancelAll(true) after prompting the user.`,
			);
			return;
		}
		for (const item of this.items()) {
			if (ACTIVE_STATUSES.includes(item.status())) this.cancel(item.id);
		}
	}

	retry(itemId: string): void {
		const item = this.items().find(i => i.id === itemId);
		if (!item || item.status() !== 'error') return;
		// Validation failures (wrong type / oversize) can't be fixed by
		// retrying — retrying would push the same invalid file into the queue.
		if (item.nonRetriable) return;
		this.clearRetry(item);
		item.status.set('queued');
		item.error.set(null);
		item.progress.set(0);
		item.retryCount.set(0);
		queueMicrotask(() => this.processQueue());
	}

	retryAllFailed(): void {
		for (const item of this.items()) {
			if (item.status() === 'error') this.retry(item.id);
		}
	}

	/** Remove a single terminal item from the list (used by the grid placeholder
	 * ✕ button on validation-error cards). No-op on non-terminal items —
	 * `cancel()` is the right entry point for those. */
	dismissItem(itemId: string): void {
		const item = this.items().find(i => i.id === itemId);
		if (!item) return;
		if (!TERMINAL_STATUSES.includes(item.status())) return;
		this.clearRetry(item);
		if (item.previewUrl) {
			URL.revokeObjectURL(item.previewUrl);
			item.previewUrl = null;
		}
		this.items.update(items => items.filter(i => i.id !== itemId));
	}

	dismiss(): void {
		const all = this.items();
		const kept: UploadItem[] = [];
		for (const item of all) {
			if (TERMINAL_STATUSES.includes(item.status())) {
				this.clearRetry(item);
				if (item.previewUrl) {
					URL.revokeObjectURL(item.previewUrl);
					item.previewUrl = null;
				}
			} else {
				kept.push(item);
			}
		}
		this.items.set(kept);
	}

	// ── Internal ──────────────────────────────────────────────────────────────

	private createItem(
		file: File,
		projectSlug: string,
		category: FileCategory,
		duplicateWarning: boolean,
		replaceMediaId?: string,
	): UploadItem {
		const previewUrl = category === 'images' ? URL.createObjectURL(file) : null;
		return {
			id: crypto.randomUUID(),
			file,
			projectSlug,
			category,
			status: signal<UploadStatus>('queued'),
			progress: signal(0),
			error: signal<string | null>(null),
			retryCount: signal(0),
			retryAfterMs: signal<number | null>(null),
			previewUrl,
			probeResult: signal<MediaMetadata | null>(null),
			duplicateWarning,
			replaceMediaId,
			nonRetriable: false,
			response: null,
			xhr: null,
			retryTimer: null,
			retryCountdown: null,
		};
	}

	/** Synchronous validation failure — terminal error, no network request,
	 * no retry button. */
	private createRejectedItem(
		file: File,
		projectSlug: string,
		category: FileCategory,
		message: string,
	): UploadItem {
		return {
			id: crypto.randomUUID(),
			file,
			projectSlug,
			category,
			status: signal<UploadStatus>('error'),
			progress: signal(0),
			error: signal<string | null>(message),
			retryCount: signal(0),
			retryAfterMs: signal<number | null>(null),
			previewUrl: null,
			probeResult: signal<MediaMetadata | null>(null),
			duplicateWarning: false,
			nonRetriable: true,
			response: null,
			xhr: null,
			retryTimer: null,
			retryCountdown: null,
		};
	}

	private readonly beforeUnloadHandler = (event: BeforeUnloadEvent): void => {
		event.preventDefault();
		// Older browsers need returnValue set to show the native prompt. Custom
		// text has been ignored since Chrome 51 / Firefox 44.
		event.returnValue = '';
	};

	private processQueue(): void {
		const items = this.items();
		let active = items.filter(
			i => i.status() === 'probing' || i.status() === 'uploading',
		).length;
		if (active >= CONCURRENCY) return;
		for (const item of items) {
			if (active >= CONCURRENCY) break;
			if (item.status() === 'queued' && item.retryAfterMs() === null) {
				void this.processItem(item);
				active++;
			}
		}
	}

	private async processItem(item: UploadItem): Promise<void> {
		if (PROBE_CATEGORIES.includes(item.category)) {
			item.status.set('probing');
			await this.acquireProbeSlot();
			try {
				// Re-check cancellation after waiting — caller may have cancelled
				// while we were queued behind the probe semaphore.
				if ((item.status() as UploadStatus) === 'cancelled') return;
				const probe = await this.probe.probeFile(item.file);
				item.probeResult.set(probe);
			} finally {
				this.releaseProbeSlot();
			}
		} else {
			item.probeResult.set({});
		}

		// Check if cancelled during probe
		if (item.status() === 'cancelled') return;

		item.status.set('uploading');
		item.progress.set(0);

		const form = new FormData();
		// Variant uploads use the multi-file backend route (`uploadMulti.array('files', 200)`
		// — kept for API compat per plan D9). The standalone route is `upload.single('file')`.
		// Field names diverge by route; pick at enqueue time based on whether this is
		// a grouped (variant) upload.
		form.append(item.groupId ? 'files' : 'file', item.file);
		const meta = item.probeResult();
		if (meta?.durationSeconds != null) form.append('durationSeconds', String(meta.durationSeconds));
		if (meta?.fps != null) form.append('fps', String(meta.fps));
		if (meta?.width != null) form.append('width', String(meta.width));
		if (meta?.height != null) form.append('height', String(meta.height));

		if (item.groupId) {
			// Variant upload: one file per HTTP request, per-file metadata carried
			// as top-level form fields (backend picks them up when `files` is a
			// single-element array — see upload.routes.ts).
			const url = `${environment.apiUrl}/api/projects/${item.projectSlug}/media/${item.groupId}/variants/upload`;
			this.xhrUpload<VariantUploadResponse>(item, url, form, body => this.onVariantSuccess(item, body));
			return;
		}

		form.append('category', item.category);
		if (item.replaceMediaId) form.append('replaceMediaId', item.replaceMediaId);
		const url = `${environment.apiUrl}/api/projects/${item.projectSlug}/media/upload`;
		this.xhrUpload<UploadResponse>(item, url, form, body => this.onUploadSuccess(item, body));
	}

	private acquireProbeSlot(): Promise<void> {
		if (this.activeProbes < PROBE_CONCURRENCY) {
			this.activeProbes++;
			return Promise.resolve();
		}
		return new Promise<void>(resolve => {
			this.probeWaiters.push(() => {
				this.activeProbes++;
				resolve();
			});
		});
	}

	private releaseProbeSlot(): void {
		this.activeProbes--;
		const next = this.probeWaiters.shift();
		if (next) next();
	}

	/**
	 * Upload via a raw XMLHttpRequest so `upload.onprogress` fires real byte
	 * progress. The app configures HttpClient with `withFetch()` (app.config.ts)
	 * and Angular's fetch backend never emits UploadProgress events — HttpClient
	 * uploads would jump straight 0 → 100. XHR is the only reliable source of
	 * upload progress in the browser. The jwtInterceptor is bypassed here, so we
	 * attach the bearer token ourselves and translate non-2xx / network failures
	 * into an HttpErrorResponse so `handleError` (retry/backoff) works unchanged.
	 */
	private xhrUpload<T>(item: UploadItem, url: string, form: FormData, onSuccess: (body: T) => void, isRetry = false): void {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', url);
		const token = this.auth.accessToken();
		if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
		xhr.responseType = 'json';
		xhr.upload.addEventListener('progress', event => {
			if (event.lengthComputable) {
				item.progress.set(Math.round((event.loaded / event.total) * 100));
			}
		});
		xhr.addEventListener('load', () => {
			item.xhr = null;
			if (xhr.status >= 200 && xhr.status < 300) {
				onSuccess(xhr.response as T);
				return;
			}
			// Mirror jwtInterceptor: a 401 triggers a single token refresh + retry.
			// refresh() is single-flight, so concurrent uploads share one refresh.
			if (xhr.status === 401 && !isRetry) {
				this.refreshAndRetry(item, url, form, onSuccess);
				return;
			}
			// A token minted seconds ago being rejected means the session is over.
			if (xhr.status === 401 && isRetry) this.auth.endSession();
			this.handleError(item, this.toHttpError(xhr, url));
		});
		// Network-level failure surfaces as status 0 — handleError treats it as transient.
		xhr.addEventListener('error', () => {
			item.xhr = null;
			this.handleError(item, this.toHttpError(xhr, url));
		});
		// abort() comes from cancel(), which has already set status='cancelled'.
		xhr.addEventListener('abort', () => { item.xhr = null; });
		item.xhr = xhr;
		xhr.send(form);
	}

	/** 401 handler mirroring jwtInterceptor: refresh the access token once, then
	 * re-send with the new token (progress restarts from 0, matching how the
	 * HttpClient retry re-emitted UploadProgress). refresh() ends the session
	 * itself on a real rejection; we surface the original 401 to the item. */
	private refreshAndRetry<T>(item: UploadItem, url: string, form: FormData, onSuccess: (body: T) => void): void {
		this.auth.refresh().subscribe({
			next: () => {
				if (item.status() === 'cancelled') return;
				item.progress.set(0);
				this.xhrUpload(item, url, form, onSuccess, true);
			},
			error: () => this.handleError(item, new HttpErrorResponse({ status: 401, statusText: 'Unauthorized', url })),
		});
	}

	private toHttpError(xhr: XMLHttpRequest, url: string): HttpErrorResponse {
		return new HttpErrorResponse({
			error: xhr.response,
			status: xhr.status,
			statusText: xhr.statusText,
			url,
		});
	}

	private onUploadSuccess(item: UploadItem, body: UploadResponse | null): void {
		if (!body) return;
		item.progress.set(100);
		if (item.previewUrl) {
			URL.revokeObjectURL(item.previewUrl);
			item.previewUrl = null;
		}
		// Response MUST be set before status — consumers watch for
		// `status === 'done'` and read item.response in the same tick.
		item.response = body;
		item.status.set('done');
		queueMicrotask(() => this.processQueue());
	}

	/** Variant uploads complete into an existing media group. The response shape
	 * differs from single-file uploads (`{ added, errors, media }`) and the
	 * consumer reacts by reloading files — not by pushing a new row. We surface
	 * per-file backend errors (e.g. duplicate variant name) as a terminal error
	 * on this item so the panel can show a retry-unfriendly failure reason. */
	private onVariantSuccess(item: UploadItem, body: VariantUploadResponse | null): void {
		if (item.previewUrl) {
			URL.revokeObjectURL(item.previewUrl);
			item.previewUrl = null;
		}
		if (body && body.added > 0) {
			item.progress.set(100);
			item.status.set('done');
		} else {
			// Backend accepted the request but rejected this variant (e.g.
			// duplicate name — the only non-retriable variant-level failure today).
			const reason = body?.errors?.[0] ?? 'Variant rejected by backend';
			item.error.set(reason);
			item.nonRetriable = true;
			item.status.set('error');
		}
		queueMicrotask(() => this.processQueue());
	}

	private handleError(item: UploadItem, err: HttpErrorResponse): void {
		item.xhr = null;
		// If the user cancelled, unsubscribe() already set 'cancelled'; swallow the aborted error.
		if (item.status() === 'cancelled') return;

		if (this.isTransient(err) && item.retryCount() < MAX_AUTO_RETRIES) {
			item.retryCount.update(n => n + 1);
			this.scheduleRetry(item);
			return;
		}
		item.status.set('error');
		item.error.set(this.formatError(err));
		// Replace uploads have no grid placeholder — surface the failure by
		// asking the upload panel to un-collapse. The panel is otherwise easy to
		// miss (collapsed by default + global toast says only "N failed").
		if (item.replaceMediaId) this.panelExpandRequest.update(n => n + 1);
		queueMicrotask(() => this.processQueue());
	}

	private scheduleRetry(item: UploadItem): void {
		const attempt = item.retryCount();
		const delay = RETRY_DELAYS_MS[attempt - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
		item.retryAfterMs.set(delay);
		item.progress.set(0);
		item.status.set('queued');

		// Tick down the countdown so the panel can render "Retrying in Ns..." live.
		// Uses a wall-clock deadline so any drift stays bounded to one tick.
		const deadline = Date.now() + delay;
		item.retryCountdown = setInterval(() => {
			const remaining = Math.max(0, deadline - Date.now());
			item.retryAfterMs.set(remaining);
		}, RETRY_COUNTDOWN_TICK_MS);

		item.retryTimer = setTimeout(() => {
			item.retryTimer = null;
			if (item.retryCountdown) {
				clearInterval(item.retryCountdown);
				item.retryCountdown = null;
			}
			item.retryAfterMs.set(null);
			this.processQueue();
		}, delay);
	}

	private clearRetry(item: UploadItem): void {
		if (item.retryTimer) {
			clearTimeout(item.retryTimer);
			item.retryTimer = null;
		}
		if (item.retryCountdown) {
			clearInterval(item.retryCountdown);
			item.retryCountdown = null;
		}
		item.retryAfterMs.set(null);
	}

	private isTransient(err: HttpErrorResponse): boolean {
		if (err.status === 0) return true;
		if (err.status >= 500 && err.status < 600) return true;
		if (err.status === 408 || err.status === 429) return true;
		return false;
	}

	private formatError(err: HttpErrorResponse): string {
		const body = (err.error ?? {}) as { error?: string };
		return body.error ?? err.message ?? 'Upload error';
	}
}
